// Phase B — inline "Link existing record to this project" picker used by the
// Compliance Hub's building-scoped module cards. Lists the client's records for
// a module, shows which are already linked to this project, and links one in a
// single PATCH (project_id). Audit-logged server-side via the module's update.
import { useEffect, useState } from "react";
import { X, Link2, Check, Search, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ComplianceLinkDialog({ open, onClose, module, project, clientId, buildingIds = [], onLinked }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [linkingId, setLinkingId] = useState(null);

    useEffect(() => {
        if (!open || !module) return;
        setLoading(true);
        api.get(`${module.endpoint}`, { params: { client_id: clientId } })
            .then((r) => setRows(r.data || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [open, module, clientId]);

    if (!open || !module) return null;

    async function link(rec) {
        setLinkingId(rec.id);
        try {
            await api.patch(`${module.endpoint}/${rec.id}`, {
                project_id: project.id,
                phase_key: project.workflow_stage || null,
                portal_visible: true,
            });
            toast.success(`Linked to ${project.name}`);
            setRows((rs) => rs.map((x) => (x.id === rec.id ? { ...x, project_id: project.id } : x)));
            onLinked && onLinked();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Link failed");
        } finally {
            setLinkingId(null);
        }
    }

    const label = (r) => module.labelFn(r) || r.reference || "Untitled";
    const inBuildings = (r) => buildingIds.length === 0 || !r.building_id || buildingIds.includes(r.building_id);
    const filtered = rows
        .filter((r) => label(r).toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => (a.project_id === project.id ? -1 : 0) - (b.project_id === project.id ? -1 : 0) || (inBuildings(b) - inBuildings(a)));

    return (
        <div data-testid="compliance-link-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(31,31,31,.45)" }} onClick={onClose}>
            <div className="bg-white rounded-[14px] shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" style={{ border: "1px solid #E5E0DA" }} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "#F0EDE8" }}>
                    <div>
                        <div className="text-[13px] font-bold text-[#1F1F1F] flex items-center gap-2"><Link2 className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Link {module.label} to project</div>
                        <div className="text-[11px] mt-0.5" style={{ color: "#9A968F" }}>Attach an existing record to {project.name}</div>
                    </div>
                    <button data-testid="link-dialog-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F6F4F0]"><X className="w-4 h-4" style={{ color: "#9A968F" }} /></button>
                </div>
                <div className="px-5 py-3 border-b" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-[9px] border" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <Search className="w-3.5 h-3.5" style={{ color: "#9A968F" }} />
                        <input data-testid="link-dialog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${module.label.toLowerCase()}…`} className="flex-1 bg-transparent outline-none text-[12.5px]" />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-10 text-[12px]" style={{ color: "#9A968F" }}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-[12px] italic py-10 text-center" style={{ color: "#9A968F" }}>No {module.label.toLowerCase()} records for this client yet.</div>
                    ) : filtered.map((r) => {
                        const linked = r.project_id === project.id;
                        return (
                            <div key={r.id} data-testid={`link-row-${r.id}`} className="flex items-center gap-3 px-3 py-2 rounded-[9px] border" style={{ borderColor: "#EFEAE3", background: linked ? "#F3F8F1" : "#fff" }}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{label(r)}</div>
                                    <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{r.reference || ""}{!inBuildings(r) && !linked ? " · other building" : ""}</div>
                                </div>
                                {linked ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: "#E8F1E6", color: "#3F7D3A" }}><Check className="w-3 h-3" /> Linked</span>
                                ) : (
                                    <button data-testid={`link-btn-${r.id}`} onClick={() => link(r)} disabled={linkingId === r.id}
                                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-[7px] text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                        {linkingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} Link
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
